import React from "react";

import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { useI18n } from "@excalidraw/excalidraw/i18n";

import {
  loginUser,
  logoutUser,
  registerUser,
  type SessionUser,
} from "../data/auth";

import "./UserSessionDialog.scss";

type Mode = "signin" | "register";
const USERNAME_PATTERN = "[A-Za-z0-9._-]{3,32}";
const PASSWORD_MIN_LENGTH = 8;

export const UserSessionDialog = ({
  user,
  onCloseRequest,
  onSignedIn,
  onSignedOut,
}: {
  user: SessionUser | null;
  onCloseRequest: () => void;
  onSignedIn: (user: SessionUser, opts: { isNewAccount: boolean }) => void;
  onSignedOut: () => void;
}) => {
  const { t } = useI18n();
  const [mode, setMode] = React.useState<Mode>("signin");
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const usernameInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (user) {
      return;
    }

    usernameInputRef.current?.focus();
  }, [user, mode]);

  const resetForm = React.useCallback(() => {
    setUsername("");
    setPassword("");
    setErrorMessage("");
  }, []);

  const submit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsSubmitting(true);
      setErrorMessage("");

      try {
        const nextUser =
          mode === "register"
            ? await registerUser(username, password)
            : await loginUser(username, password);
        resetForm();
        onSignedIn(nextUser, {
          isNewAccount: mode === "register",
        });
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : "Request failed",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [mode, onSignedIn, password, resetForm, t, username],
  );

  const handleLogout = React.useCallback(async () => {
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await logoutUser();
      onSignedOut();
      onCloseRequest();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Request failed",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [onCloseRequest, onSignedOut, t]);

  return (
    <Dialog
      onCloseRequest={onCloseRequest}
      title={t("auth.title")}
      size="small"
    >
      <div className="UserSessionDialog">
        <p className="UserSessionDialog__description">
          {t("auth.description")}
        </p>
        {user ? (
          <div className="UserSessionDialog__signedIn">
            <div className="UserSessionDialog__signedInLabel">
              {t("auth.signedInAs", {
                username: user.username,
              })}
            </div>
            <FilledButton
              label={t("auth.logoutAction")}
              onClick={handleLogout}
              disabled={isSubmitting}
              fullWidth
            >
              {t("auth.logoutAction")}
            </FilledButton>
          </div>
        ) : (
          <form className="UserSessionDialog__form" onSubmit={submit}>
            <div className="UserSessionDialog__tabs" role="tablist">
              <button
                type="button"
                className="UserSessionDialog__tab"
                data-active={mode === "signin"}
                onClick={() => {
                  setMode("signin");
                  setErrorMessage("");
                }}
              >
                {t("auth.signInTab")}
              </button>
              <button
                type="button"
                className="UserSessionDialog__tab"
                data-active={mode === "register"}
                onClick={() => {
                  setMode("register");
                  setErrorMessage("");
                }}
              >
                {t("auth.registerTab")}
              </button>
            </div>

            <label className="UserSessionDialog__field">
              <span>{t("auth.username")}</span>
              <input
                ref={usernameInputRef}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoCapitalize="none"
                autoComplete="username"
                maxLength={32}
                pattern={USERNAME_PATTERN}
                spellCheck={false}
                title={t("auth.usernameHint")}
                required
              />
              <small className="UserSessionDialog__hint">
                {t("auth.usernameHint")}
              </small>
            </label>

            <label className="UserSessionDialog__field">
              <span>{t("auth.password")}</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete={
                  mode === "register" ? "new-password" : "current-password"
                }
                minLength={PASSWORD_MIN_LENGTH}
                required
              />
              <small className="UserSessionDialog__hint">
                {t("auth.passwordHint", {
                  minLength: PASSWORD_MIN_LENGTH,
                })}
              </small>
            </label>

            {errorMessage && (
              <p className="UserSessionDialog__error">{errorMessage}</p>
            )}

            <button
              type="submit"
              className="UserSessionDialog__submit"
              disabled={isSubmitting}
            >
              {mode === "register"
                ? t("auth.registerAction")
                : t("auth.signInAction")}
            </button>
          </form>
        )}
      </div>
    </Dialog>
  );
};
